import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FaCheckCircle,
    FaEye,
    FaEyeSlash,
    FaSearch,
    FaSyncAlt,
    FaTimesCircle,
} from "react-icons/fa";

import Swal from "sweetalert2";

import api from "../api/api";

const PAGE_SIZE = 20;

const BRAND = {
    navy: "#062A4B",
    blue: "#063A6B",
    gold: "#F6C944",
    green: "#4FAE22",
    red: "#C62828",
    gray: "#64748B",
    light: "#F4F7FA",
    border: "#DCE3EA",
};

const EntranceResult = () => {
    const [applicants, setApplicants] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [publicationLoading, setPublicationLoading] =
        useState(true);

    const [publicationBusy, setPublicationBusy] =
        useState(false);

    const [published, setPublished] =
        useState(false);

    const [publishedAt, setPublishedAt] =
        useState(null);

    const [error, setError] =
        useState("");

    const [searchInput, setSearchInput] =
        useState("");

    const [search, setSearch] =
        useState("");

    const [page, setPage] =
        useState(1);

    const [count, setCount] =
        useState(0);

    const [next, setNext] =
        useState(null);

    const [previous, setPrevious] =
        useState(null);

    const totalPages = useMemo(
        () =>
            Math.max(
                1,
                Math.ceil(
                    count /
                        PAGE_SIZE
                )
            ),
        [count]
    );

    const loadPublicationStatus =
        useCallback(
            async () => {
                try {
                    setPublicationLoading(
                        true
                    );

                    const response =
                        await api.get(
                            "/api/entrance_examination/admin/results/publication/"
                        );

                    const data =
                        response?.data ||
                        {};

                    setPublished(
                        Boolean(
                            data.is_published
                        )
                    );

                    setPublishedAt(
                        data.published_at ||
                            null
                    );
                } catch (
                    requestError
                ) {
                    console.error(
                        "Entrance publication status loading failed:",
                        requestError
                    );
                } finally {
                    setPublicationLoading(
                        false
                    );
                }
            },
            []
        );

    const loadResults =
        useCallback(
            async (
                requestedPage = page,
                requestedSearch = search
            ) => {
                setLoading(true);
                setError("");

                try {
                    const response =
                        await api.get(
                            "/api/entrance_examination/admin/papers/",
                            {
                                params: {
                                    page:
                                        requestedPage,
                                    page_size:
                                        PAGE_SIZE,
                                    search:
                                        requestedSearch,
                                },
                            }
                        );

                    const data =
                        response?.data ||
                        {};

                    const completed =
                        (
                            data.results ||
                            []
                        ).filter(
                            (item) =>
                                item.math_score !==
                                    null &&
                                item.math_score !==
                                    undefined &&
                                item.english_score !==
                                    null &&
                                item.english_score !==
                                    undefined
                        );

                    setApplicants(
                        completed
                    );

                    setCount(
                        Number(
                            data.count ||
                                0
                        )
                    );

                    setNext(
                        data.next ||
                            null
                    );

                    setPrevious(
                        data.previous ||
                            null
                    );
                } catch (
                    requestError
                ) {
                    console.error(
                        "Entrance results loading failed:",
                        requestError
                    );

                    setError(
                        requestError
                            ?.response
                            ?.data
                            ?.detail ||
                            "Unable to load entrance results."
                    );
                } finally {
                    setLoading(false);
                }
            },
            [page, search]
        );

    const togglePublication =
        async () => {
            const action = published
                ? "unpublish"
                : "publish";

            const title =
                published
                    ? "Unpublish Entrance Results?"
                    : "Publish Entrance Results?";

            const text =
                published
                    ? "The public will no longer be able to see the entrance results."
                    : "Only students who passed will be publicly listed. Failed students will remain hidden.";

            const result =
                await Swal.fire({
                    title,
                    text,
                    icon: published
                        ? "warning"
                        : "question",
                    showCancelButton: true,
                    confirmButtonText:
                        published
                            ? "Yes, Unpublish"
                            : "Yes, Publish",
                    cancelButtonText:
                        "Cancel",
                    confirmButtonColor:
                        published
                            ? BRAND.red
                            : BRAND.green,
                    cancelButtonColor:
                        "#64748B",
                });

            if (!result.isConfirmed) {
                return;
            }

            try {
                setPublicationBusy(
                    true
                );

                await api.post(
                    "/api/entrance_examination/admin/results/publication/",
                    {
                        action,
                    }
                );

                setPublished(
                    action ===
                        "publish"
                );

                if (
                    action ===
                    "publish"
                ) {
                    setPublishedAt(
                        new Date().toISOString()
                    );
                } else {
                    setPublishedAt(
                        null
                    );
                }

                await Swal.fire({
                    icon: "success",
                    title:
                        action ===
                        "publish"
                            ? "Results Published"
                            : "Results Unpublished",
                    text:
                        action ===
                        "publish"
                            ? "Passed entrance examination results are now publicly available."
                            : "Entrance examination results have been removed from the public results page.",
                    confirmButtonColor:
                        BRAND.navy,
                });
            } catch (
                requestError
            ) {
                console.error(
                    "Entrance result publication action failed:",
                    requestError
                );

                await Swal.fire({
                    icon: "error",
                    title:
                        "Action Failed",
                    text:
                        requestError
                            ?.response
                            ?.data
                            ?.detail ||
                        "Unable to change the publication status.",
                    confirmButtonColor:
                        BRAND.navy,
                });
            } finally {
                setPublicationBusy(
                    false
                );
            }
        };

    useEffect(() => {
        loadPublicationStatus();
    }, [
        loadPublicationStatus,
    ]);

    useEffect(() => {
        loadResults(
            page,
            search
        );
    }, [
        page,
        search,
        loadResults,
    ]);

    useEffect(() => {
        const timer =
            window.setTimeout(
                () => {
                    setPage(1);
                    setSearch(
                        searchInput.trim()
                    );
                },
                400
            );

        return () =>
            window.clearTimeout(
                timer
            );
    }, [searchInput]);

    return (
        <div
            style={{
                minHeight: "100vh",
                background:
                    BRAND.light,
                padding: 20,
            }}
        >
            <style>
                {`
                    .entrance-result-card {
                        background: #fff;
                        border: 1px solid ${BRAND.border};
                        border-radius: 14px;
                        overflow: hidden;
                        box-shadow:
                            0 5px 18px
                            rgba(6,42,75,.07);
                    }

                    .entrance-result-table {
                        width: 100%;
                        border-collapse: collapse;
                        min-width: 950px;
                    }

                    .entrance-result-table th {
                        background: ${BRAND.navy};
                        color: white;
                        padding: 14px;
                        text-align: left;
                        font-size: 13px;
                        white-space: nowrap;
                    }

                    .entrance-result-table td {
                        padding: 14px;
                        border-bottom:
                            1px solid ${BRAND.border};
                        font-size: 13px;
                    }

                    .entrance-result-table tbody tr:hover {
                        background: #f8fafc;
                    }

                    .result-search-wrap {
                        position: relative;
                        flex: 1;
                        min-width: 250px;
                    }

                    .result-search {
                        width: 100%;
                        border:
                            1px solid ${BRAND.border};
                        border-radius: 9px;
                        padding:
                            11px 14px 11px 40px;
                        font-size: 14px;
                        outline: none;
                        box-sizing: border-box;
                    }

                    .result-search:focus {
                        border-color:
                            ${BRAND.blue};
                        box-shadow:
                            0 0 0 3px
                            rgba(6,58,107,.08);
                    }

                    .result-pagination {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding: 15px;
                        gap: 12px;
                        flex-wrap: wrap;
                    }

                    .result-page-button {
                        border:
                            1px solid ${BRAND.border};
                        background: #fff;
                        color: ${BRAND.navy};
                        border-radius: 7px;
                        padding: 8px 13px;
                        font-weight: 700;
                        cursor: pointer;
                    }

                    .result-page-button:disabled {
                        opacity: .4;
                        cursor: not-allowed;
                    }

                    .publish-result-button {
                        border: none;
                        border-radius: 8px;
                        padding: 10px 15px;
                        color: #fff;
                        cursor: pointer;
                        font-weight: 800;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: 7px;
                    }

                    .publish-result-button:disabled {
                        opacity: .55;
                        cursor: not-allowed;
                    }

                    @media (max-width: 700px) {
                        .result-search-wrap {
                            width: 100%;
                            min-width: 100%;
                        }

                        .publish-result-button {
                            width: 100%;
                        }
                    }
                `}
            </style>

            <div
                style={{
                    maxWidth: 1400,
                    margin: "0 auto",
                }}
            >
                <div
                    style={{
                        background:
                            BRAND.navy,
                        color: "#fff",
                        borderRadius: 14,
                        padding: 23,
                        marginBottom: 18,
                    }}
                >
                    <div
                        style={{
                            display:
                                "flex",
                            justifyContent:
                                "space-between",
                            alignItems:
                                "center",
                            gap: 15,
                            flexWrap:
                                "wrap",
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    fontSize: 24,
                                    fontWeight: 800,
                                }}
                            >
                                Entrance Results
                            </div>

                            <div
                                style={{
                                    opacity: 0.8,
                                    marginTop: 5,
                                }}
                            >
                                Manage and publish
                                entrance examination
                                results
                            </div>
                        </div>

                        <div
                            style={{
                                display:
                                    "flex",
                                alignItems:
                                    "center",
                                gap: 9,
                                flexWrap:
                                    "wrap",
                            }}
                        >
                            <button
                                className="publish-result-button"
                                onClick={
                                    togglePublication
                                }
                                disabled={
                                    publicationBusy ||
                                    publicationLoading
                                }
                                style={{
                                    background:
                                        published
                                            ? BRAND.red
                                            : BRAND.green,
                                }}
                            >
                                {published ? (
                                    <FaEyeSlash />
                                ) : (
                                    <FaEye />
                                )}

                                {publicationBusy
                                    ? "Processing..."
                                    : published
                                    ? "Unpublish Entrance Results"
                                    : "Publish Entrance Results"}
                            </button>

                            <button
                                onClick={() => {
                                    loadResults(
                                        page,
                                        search
                                    );
                                    loadPublicationStatus();
                                }}
                                style={{
                                    background:
                                        "rgba(255,255,255,.1)",
                                    border:
                                        "1px solid rgba(255,255,255,.25)",
                                    color:
                                        "#fff",
                                    padding:
                                        "9px 13px",
                                    borderRadius:
                                        8,
                                    cursor:
                                        "pointer",
                                    fontWeight:
                                        700,
                                }}
                            >
                                <FaSyncAlt
                                    style={{
                                        marginRight:
                                            7,
                                    }}
                                />
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        background:
                            published
                                ? "#ECF8EF"
                                : "#F8FAFC",
                        border:
                            `1px solid ${
                                published
                                    ? "#B9DEBF"
                                    : BRAND.border
                            }`,
                        borderRadius: 10,
                        padding: 13,
                        marginBottom: 15,
                        display:
                            "flex",
                        alignItems:
                            "center",
                        gap: 10,
                    }}
                >
                    {published ? (
                        <FaCheckCircle
                            style={{
                                color:
                                    BRAND.green,
                            }}
                        />
                    ) : (
                        <FaEyeSlash
                            style={{
                                color:
                                    BRAND.gray,
                            }}
                        />
                    )}

                    <div>
                        <strong
                            style={{
                                color:
                                    published
                                        ? BRAND.green
                                        : BRAND.navy,
                            }}
                        >
                            {published
                                ? "Public Results: PUBLISHED"
                                : "Public Results: UNPUBLISHED"}
                        </strong>

                        <div
                            style={{
                                color:
                                    BRAND.gray,
                                fontSize: 12,
                                marginTop: 2,
                            }}
                        >
                            {published
                                ? "Passed applicants are currently visible on the public entrance results page."
                                : "Entrance results are currently hidden from the public."}
                        </div>
                    </div>
                </div>

                <div className="entrance-result-card">
                    <div
                        style={{
                            padding: 16,
                            borderBottom:
                                `1px solid ${BRAND.border}`,
                            display:
                                "flex",
                            alignItems:
                                "center",
                            gap: 15,
                            flexWrap:
                                "wrap",
                        }}
                    >
                        <div className="result-search-wrap">
                            <FaSearch
                                style={{
                                    position:
                                        "absolute",
                                    left: 14,
                                    top: 13,
                                    color:
                                        BRAND.gray,
                                }}
                            />

                            <input
                                className="result-search"
                                value={
                                    searchInput
                                }
                                onChange={(
                                    event
                                ) =>
                                    setSearchInput(
                                        event
                                            .target
                                            .value
                                    )
                                }
                                placeholder="Search applicant name or application number..."
                            />
                        </div>

                        <div
                            style={{
                                fontSize: 13,
                                color:
                                    BRAND.gray,
                            }}
                        >
                            Passing mark:
                            <strong
                                style={{
                                    color:
                                        BRAND.navy,
                                    marginLeft: 5,
                                }}
                            >
                                50.00
                            </strong>
                        </div>
                    </div>

                    {error && (
                        <div
                            style={{
                                margin: 15,
                                padding: 13,
                                borderRadius: 8,
                                background:
                                    "#FFF1F1",
                                color:
                                    BRAND.red,
                            }}
                        >
                            {error}
                        </div>
                    )}

                    <div
                        style={{
                            overflowX:
                                "auto",
                        }}
                    >
                        <table className="entrance-result-table">
                            <thead>
                                <tr>
                                    <th>
                                        #
                                    </th>

                                    <th>
                                        Applicant
                                    </th>

                                    <th>
                                        Application
                                    </th>

                                    <th>
                                        Mathematics
                                    </th>

                                    <th>
                                        English
                                    </th>

                                    <th>
                                        Average
                                    </th>

                                    <th>
                                        Decision
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td
                                            colSpan={
                                                7
                                            }
                                            style={{
                                                textAlign:
                                                    "center",
                                                padding:
                                                    50,
                                            }}
                                        >
                                            Loading
                                            entrance
                                            results...
                                        </td>
                                    </tr>
                                ) : applicants.length ===
                                  0 ? (
                                    <tr>
                                        <td
                                            colSpan={
                                                7
                                            }
                                            style={{
                                                textAlign:
                                                    "center",
                                                padding:
                                                    50,
                                                color:
                                                    BRAND.gray,
                                            }}
                                        >
                                            No completed
                                            entrance
                                            results found.
                                        </td>
                                    </tr>
                                ) : (
                                    applicants.map(
                                        (
                                            applicant,
                                            index
                                        ) => {
                                            const average =
                                                Number(
                                                    applicant.average_score ||
                                                        0
                                                );

                                            const passed =
                                                average >=
                                                50;

                                            return (
                                                <tr
                                                    key={
                                                        applicant.application_number ||
                                                        index
                                                    }
                                                >
                                                    <td>
                                                        {(page -
                                                            1) *
                                                            PAGE_SIZE +
                                                            index +
                                                            1}
                                                    </td>

                                                    <td>
                                                        <strong>
                                                            {
                                                                applicant.applicant_name
                                                            }
                                                        </strong>
                                                    </td>

                                                    <td>
                                                        {
                                                            applicant.application_number
                                                        }
                                                    </td>

                                                    <td>
                                                        <strong>
                                                            {
                                                                applicant.math_score
                                                            }
                                                        </strong>
                                                        /
                                                        {applicant
                                                            .mathematics
                                                            ?.total_marks ||
                                                            70}
                                                    </td>

                                                    <td>
                                                        <strong>
                                                            {
                                                                applicant.english_score
                                                            }
                                                        </strong>
                                                        /
                                                        {applicant
                                                            .english
                                                            ?.total_marks ||
                                                            70}
                                                    </td>

                                                    <td>
                                                        <strong
                                                            style={{
                                                                fontSize:
                                                                    16,
                                                            }}
                                                        >
                                                            {average.toFixed(
                                                                2
                                                            )}
                                                        </strong>
                                                    </td>

                                                    <td>
                                                        {passed ? (
                                                            <span
                                                                style={{
                                                                    color:
                                                                        BRAND.green,
                                                                    fontWeight:
                                                                        800,
                                                                }}
                                                            >
                                                                <FaCheckCircle
                                                                    style={{
                                                                        marginRight:
                                                                            6,
                                                                    }}
                                                                />
                                                                PASSED
                                                            </span>
                                                        ) : (
                                                            <span
                                                                style={{
                                                                    color:
                                                                        BRAND.red,
                                                                    fontWeight:
                                                                        800,
                                                                }}
                                                            >
                                                                <FaTimesCircle
                                                                    style={{
                                                                        marginRight:
                                                                            6,
                                                                    }}
                                                                />
                                                                FAILED
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="result-pagination">
                        <div
                            style={{
                                color:
                                    BRAND.gray,
                                fontSize: 13,
                            }}
                        >
                            Page{" "}
                            <strong>
                                {page}
                            </strong>{" "}
                            of{" "}
                            <strong>
                                {totalPages}
                            </strong>
                        </div>

                        <div
                            style={{
                                display:
                                    "flex",
                                gap: 8,
                            }}
                        >
                            <button
                                className="result-page-button"
                                disabled={
                                    !previous ||
                                    page <=
                                        1 ||
                                    loading
                                }
                                onClick={() =>
                                    setPage(
                                        (
                                            value
                                        ) =>
                                            Math.max(
                                                1,
                                                value -
                                                    1
                                            )
                                    )
                                }
                            >
                                Previous
                            </button>

                            <button
                                className="result-page-button"
                                disabled={
                                    !next ||
                                    page >=
                                        totalPages ||
                                    loading
                                }
                                onClick={() =>
                                    setPage(
                                        (
                                            value
                                        ) =>
                                            Math.min(
                                                totalPages,
                                                value +
                                                    1
                                            )
                                    )
                                }
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EntranceResult;



// import React, {
//     useCallback,
//     useEffect,
//     useMemo,
//     useState,
// } from "react";
// import {
//     FaCheckCircle,
//     FaSearch,
//     FaSyncAlt,
//     FaTimesCircle,
// } from "react-icons/fa";

// import api from "../api/api";

// const PAGE_SIZE = 20;

// const BRAND = {
//     navy: "#062A4B",
//     blue: "#063A6B",
//     gold: "#F6C944",
//     green: "#4FAE22",
//     red: "#C62828",
//     gray: "#64748B",
//     light: "#F4F7FA",
//     border: "#DCE3EA",
// };

// const EntranceResult = () => {
//     const [applicants, setApplicants] =
//         useState([]);

//     const [loading, setLoading] =
//         useState(true);

//     const [error, setError] =
//         useState("");

//     const [searchInput, setSearchInput] =
//         useState("");

//     const [search, setSearch] =
//         useState("");

//     const [page, setPage] =
//         useState(1);

//     const [count, setCount] =
//         useState(0);

//     const [next, setNext] =
//         useState(null);

//     const [previous, setPrevious] =
//         useState(null);

//     const totalPages = useMemo(
//         () =>
//             Math.max(
//                 1,
//                 Math.ceil(
//                     count / PAGE_SIZE
//                 )
//             ),
//         [count]
//     );

//     const loadResults =
//         useCallback(
//             async (
//                 requestedPage = page,
//                 requestedSearch = search
//             ) => {
//                 setLoading(true);
//                 setError("");

//                 try {
//                     const response =
//                         await api.get(
//                             "/api/entrance_examination/admin/papers/",
//                             {
//                                 params: {
//                                     page:
//                                         requestedPage,
//                                     page_size:
//                                         PAGE_SIZE,
//                                     search:
//                                         requestedSearch,
//                                 },
//                             }
//                         );

//                     const data =
//                         response?.data ||
//                         {};

//                     const completed =
//                         (
//                             data.results ||
//                             []
//                         ).filter(
//                             (item) =>
//                                 item.math_score !==
//                                     null &&
//                                 item.english_score !==
//                                     null
//                         );

//                     setApplicants(
//                         completed
//                     );

//                     setCount(
//                         Number(
//                             data.count ||
//                                 0
//                         )
//                     );

//                     setNext(
//                         data.next ||
//                             null
//                     );

//                     setPrevious(
//                         data.previous ||
//                             null
//                     );
//                 } catch (
//                     requestError
//                 ) {
//                     console.error(
//                         "Entrance results loading failed:",
//                         requestError
//                     );

//                     setError(
//                         requestError
//                             ?.response
//                             ?.data
//                             ?.detail ||
//                         "Unable to load entrance results."
//                     );
//                 } finally {
//                     setLoading(false);
//                 }
//             },
//             [page, search]
//         );

//     useEffect(() => {
//         const timer =
//             window.setTimeout(
//                 () => {
//                     setPage(1);
//                     setSearch(
//                         searchInput.trim()
//                     );
//                 },
//                 400
//             );

//         return () =>
//             window.clearTimeout(
//                 timer
//             );
//     }, [searchInput]);

//     useEffect(() => {
//         loadResults(
//             page,
//             search
//         );
//     }, [
//         page,
//         search,
//         loadResults,
//     ]);

//     return (
//         <div
//             style={{
//                 minHeight: "100vh",
//                 background:
//                     BRAND.light,
//                 padding: 20,
//             }}
//         >
//             <style>
//                 {`
//                     .entrance-result-card {
//                         background: #fff;
//                         border: 1px solid ${BRAND.border};
//                         border-radius: 14px;
//                         overflow: hidden;
//                         box-shadow: 0 5px 18px rgba(6,42,75,.07);
//                     }

//                     .entrance-result-table {
//                         width: 100%;
//                         border-collapse: collapse;
//                         min-width: 950px;
//                     }

//                     .entrance-result-table th {
//                         background: ${BRAND.navy};
//                         color: white;
//                         padding: 14px;
//                         text-align: left;
//                         font-size: 13px;
//                         white-space: nowrap;
//                     }

//                     .entrance-result-table td {
//                         padding: 14px;
//                         border-bottom: 1px solid ${BRAND.border};
//                         font-size: 13px;
//                     }

//                     .entrance-result-table tbody tr:hover {
//                         background: #f8fafc;
//                     }

//                     .result-search-wrap {
//                         position: relative;
//                         flex: 1;
//                         min-width: 250px;
//                     }

//                     .result-search {
//                         width: 100%;
//                         border: 1px solid ${BRAND.border};
//                         border-radius: 9px;
//                         padding: 11px 14px 11px 40px;
//                         font-size: 14px;
//                         outline: none;
//                     }

//                     .result-search:focus {
//                         border-color: ${BRAND.blue};
//                         box-shadow: 0 0 0 3px rgba(6,58,107,.08);
//                     }

//                     .result-pagination {
//                         display: flex;
//                         justify-content: space-between;
//                         align-items: center;
//                         padding: 15px;
//                         gap: 12px;
//                         flex-wrap: wrap;
//                     }

//                     .result-page-button {
//                         border: 1px solid ${BRAND.border};
//                         background: #fff;
//                         color: ${BRAND.navy};
//                         border-radius: 7px;
//                         padding: 8px 13px;
//                         font-weight: 700;
//                         cursor: pointer;
//                     }

//                     .result-page-button:disabled {
//                         opacity: .4;
//                         cursor: not-allowed;
//                     }
//                 `}
//             </style>

//             <div
//                 style={{
//                     maxWidth: 1400,
//                     margin: "0 auto",
//                 }}
//             >
//                 <div
//                     style={{
//                         background:
//                             BRAND.navy,
//                         color: "#fff",
//                         borderRadius: 14,
//                         padding: 23,
//                         marginBottom: 18,
//                     }}
//                 >
//                     <div
//                         style={{
//                             display:
//                                 "flex",
//                             justifyContent:
//                                 "space-between",
//                             alignItems:
//                                 "center",
//                             gap: 15,
//                             flexWrap:
//                                 "wrap",
//                         }}
//                     >
//                         <div>
//                             <div
//                                 style={{
//                                     fontSize: 24,
//                                     fontWeight: 800,
//                                 }}
//                             >
//                                 Entrance Results
//                             </div>

//                             <div
//                                 style={{
//                                     opacity:
//                                         .8,
//                                     marginTop: 5,
//                                 }}
//                             >
//                                 Final admission
//                                 entrance examination
//                                 results
//                             </div>
//                         </div>

//                         <button
//                             onClick={() =>
//                                 loadResults(
//                                     page,
//                                     search
//                                 )
//                             }
//                             style={{
//                                 background:
//                                     "rgba(255,255,255,.1)",
//                                 border:
//                                     "1px solid rgba(255,255,255,.25)",
//                                 color:
//                                     "#fff",
//                                 padding:
//                                     "9px 13px",
//                                 borderRadius:
//                                     8,
//                                 cursor:
//                                     "pointer",
//                                 fontWeight:
//                                     700,
//                             }}
//                         >
//                             <FaSyncAlt
//                                 style={{
//                                     marginRight:
//                                         7,
//                                 }}
//                             />
//                             Refresh
//                         </button>
//                     </div>
//                 </div>

//                 <div className="entrance-result-card">
//                     <div
//                         style={{
//                             padding: 16,
//                             borderBottom:
//                                 `1px solid ${BRAND.border}`,
//                             display:
//                                 "flex",
//                             alignItems:
//                                 "center",
//                             gap: 15,
//                             flexWrap:
//                                 "wrap",
//                         }}
//                     >
//                         <div className="result-search-wrap">
//                             <FaSearch
//                                 style={{
//                                     position:
//                                         "absolute",
//                                     left: 14,
//                                     top: 13,
//                                     color:
//                                         BRAND.gray,
//                                 }}
//                             />

//                             <input
//                                 className="result-search"
//                                 value={
//                                     searchInput
//                                 }
//                                 onChange={(
//                                     event
//                                 ) =>
//                                     setSearchInput(
//                                         event
//                                             .target
//                                             .value
//                                     )
//                                 }
//                                 placeholder="Search applicant name or application number..."
//                             />
//                         </div>

//                         <div
//                             style={{
//                                 fontSize: 13,
//                                 color:
//                                     BRAND.gray,
//                             }}
//                         >
//                             Passing mark:
//                             <strong
//                                 style={{
//                                     color:
//                                         BRAND.navy,
//                                     marginLeft: 5,
//                                 }}
//                             >
//                                 50.00
//                             </strong>
//                         </div>
//                     </div>

//                     {error && (
//                         <div
//                             style={{
//                                 margin: 15,
//                                 padding: 13,
//                                 borderRadius: 8,
//                                 background:
//                                     "#FFF1F1",
//                                 color:
//                                     BRAND.red,
//                             }}
//                         >
//                             {error}
//                         </div>
//                     )}

//                     <div
//                         style={{
//                             overflowX:
//                                 "auto",
//                         }}
//                     >
//                         <table className="entrance-result-table">
//                             <thead>
//                                 <tr>
//                                     <th>#</th>
//                                     <th>
//                                         Applicant
//                                     </th>
//                                     <th>
//                                         Application
//                                     </th>
//                                     <th>
//                                         Mathematics
//                                     </th>
//                                     <th>
//                                         English
//                                     </th>
//                                     <th>
//                                         Average
//                                     </th>
//                                     <th>
//                                         Decision
//                                     </th>
//                                 </tr>
//                             </thead>

//                             <tbody>
//                                 {loading ? (
//                                     <tr>
//                                         <td
//                                             colSpan={
//                                                 7
//                                             }
//                                             style={{
//                                                 textAlign:
//                                                     "center",
//                                                 padding:
//                                                     50,
//                                             }}
//                                         >
//                                             Loading
//                                             entrance
//                                             results...
//                                         </td>
//                                     </tr>
//                                 ) : applicants.length ===
//                                   0 ? (
//                                     <tr>
//                                         <td
//                                             colSpan={
//                                                 7
//                                             }
//                                             style={{
//                                                 textAlign:
//                                                     "center",
//                                                 padding:
//                                                     50,
//                                                 color:
//                                                     BRAND.gray,
//                                             }}
//                                         >
//                                             No completed
//                                             entrance
//                                             results found.
//                                         </td>
//                                     </tr>
//                                 ) : (
//                                     applicants.map(
//                                         (
//                                             applicant,
//                                             index
//                                         ) => {
//                                             const average =
//                                                 Number(
//                                                     applicant.average_score ||
//                                                         0
//                                                 );

//                                             const passed =
//                                                 average >=
//                                                 50;

//                                             return (
//                                                 <tr
//                                                     key={
//                                                         applicant.application_number
//                                                     }
//                                                 >
//                                                     <td>
//                                                         {(
//                                                             page -
//                                                             1
//                                                         ) *
//                                                             PAGE_SIZE +
//                                                             index +
//                                                             1}
//                                                     </td>

//                                                     <td>
//                                                         <strong>
//                                                             {
//                                                                 applicant.applicant_name
//                                                             }
//                                                         </strong>
//                                                     </td>

//                                                     <td>
//                                                         {
//                                                             applicant.application_number
//                                                         }
//                                                     </td>

//                                                     <td>
//                                                         <strong>
//                                                             {
//                                                                 applicant.math_score
//                                                             }
//                                                         </strong>
//                                                         /
//                                                         {
//                                                             applicant.mathematics
//                                                                 ?.total_marks
//                                                         }
//                                                     </td>

//                                                     <td>
//                                                         <strong>
//                                                             {
//                                                                 applicant.english_score
//                                                             }
//                                                         </strong>
//                                                         /
//                                                         {
//                                                             applicant.english
//                                                                 ?.total_marks
//                                                         }
//                                                     </td>

//                                                     <td>
//                                                         <strong
//                                                             style={{
//                                                                 fontSize:
//                                                                     16,
//                                                             }}
//                                                         >
//                                                             {average.toFixed(
//                                                                 2
//                                                             )}
//                                                         </strong>
//                                                     </td>

//                                                     <td>
//                                                         {passed ? (
//                                                             <span
//                                                                 style={{
//                                                                     color:
//                                                                         BRAND.green,
//                                                                     fontWeight:
//                                                                         800,
//                                                                 }}
//                                                             >
//                                                                 <FaCheckCircle
//                                                                     style={{
//                                                                         marginRight:
//                                                                             6,
//                                                                     }}
//                                                                 />
//                                                                 PASSED
//                                                             </span>
//                                                         ) : (
//                                                             <span
//                                                                 style={{
//                                                                     color:
//                                                                         BRAND.red,
//                                                                     fontWeight:
//                                                                         800,
//                                                                 }}
//                                                             >
//                                                                 <FaTimesCircle
//                                                                     style={{
//                                                                         marginRight:
//                                                                             6,
//                                                                     }}
//                                                                 />
//                                                                 FAILED
//                                                             </span>
//                                                         )}
//                                                     </td>
//                                                 </tr>
//                                             );
//                                         }
//                                     )
//                                 )}
//                             </tbody>
//                         </table>
//                     </div>

//                     <div className="result-pagination">
//                         <div
//                             style={{
//                                 color:
//                                     BRAND.gray,
//                                 fontSize: 13,
//                             }}
//                         >
//                             Page{" "}
//                             <strong>
//                                 {page}
//                             </strong>{" "}
//                             of{" "}
//                             <strong>
//                                 {totalPages}
//                             </strong>
//                         </div>

//                         <div
//                             style={{
//                                 display:
//                                     "flex",
//                                 gap: 8,
//                             }}
//                         >
//                             <button
//                                 className="result-page-button"
//                                 disabled={
//                                     !previous ||
//                                     page <= 1 ||
//                                     loading
//                                 }
//                                 onClick={() =>
//                                     setPage(
//                                         (
//                                             value
//                                         ) =>
//                                             Math.max(
//                                                 1,
//                                                 value -
//                                                     1
//                                             )
//                                     )
//                                 }
//                             >
//                                 Previous
//                             </button>

//                             <button
//                                 className="result-page-button"
//                                 disabled={
//                                     !next ||
//                                     page >=
//                                         totalPages ||
//                                     loading
//                                 }
//                                 onClick={() =>
//                                     setPage(
//                                         (
//                                             value
//                                         ) =>
//                                             Math.min(
//                                                 totalPages,
//                                                 value +
//                                                     1
//                                             )
//                                     )
//                                 }
//                             >
//                                 Next
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// };

// export default EntranceResult;